import {
  Mutation as VuexMutation,
  Action as VuexAction,
  Getter as VuexGetter,
  ActionContext as VuexActionContext,
  CommitOptions as VuexCommitOptions,
  DispatchOptions as VuexDispatchOptions,
} from 'vuex';

import { User, Token, TypedObject, ModalState, Room, Appointment, Company, AlertState, DayOverview } from '@/definitions';

export type LocalUser = Omit<User, 'password'>;
export interface RootState {
  user: LocalUser;
  token: Token;
  modal: ModalState;
  date: { date: Date };
  appointments: Room[];
  myAppointments: Appointment[];
  weekOverview: DayOverview[];
  company: { company: Company; rooms: Room[] };
  alert: AlertState;
};

export enum Mutation {
  STORE_TOKEN = 'STORE_TOKEN',
  STORE_USER = 'STORE_USER',
  STORE_COMPANY = 'STORE_COMPANY',
  SET_MODAL_STATE = 'SET_MODAL_STATE',
  SET_ALERT_STATE = 'SET_ALERT_STATE',
  SET_CURRENT_DATE = 'SET_CURRENT_DATE',
  SET_APPOINTMENTS = 'SET_APPOINTMENTS',
  SET_MY_APPOINTMENTS = 'SET_MY_APPOINTMENTS',
  SET_WEEK_OVERVIEW = 'SET_WEEK_OVERVIEW'
};

export enum Action {
  UPDATE_USER = 'UPDATE_USER',
  UPDATE_USERTOKEN = 'UPDATE_USERTOKEN',
  LOGOUT_USER = 'LOGOUT_USER',
  SHOW_MODAL = 'SHOW_MODAL',
  HIDE_MODAL = 'HIDE_MODAL',
  SHOW_ALERT = 'SHOW_ALERT',
  HIDE_ALERT = 'HIDE_ALERT',
  SET_NEW_DATE = 'SET_NEW_DATE',
  SAVE_APPOINTMENTS = 'SAVE_APPOINTMENTS',
  SAVE_MY_APPOINTMENTS = 'SAVE_MY_APPOINTMENTS',
  SAVE_WEEK_OVERVIEW = 'SAVE_WEEK_OVERVIEW',
  UPDATE_COMPANY = 'UPDATE_COMPANY'
};

export enum Getter {
  TOKEN = 'TOKEN',
  USERNAME = 'USERNAME',
  MODAL_ACTIVE = 'MODAL_ACTIVE',
};

export type Mutations<LocalState> = {
  [key in Mutation]: VuexMutation<LocalState>;
};

export type Actions<LocalState> = {
  [key in Action]: VuexAction<LocalState, RootState>;
};

export type Getters<LocalState> = {
  [key in Getter]: VuexGetter<LocalState, RootState>;
};

export type LocalContext<LocalState> = ActionContext<LocalState, RootState>;

type InternalCommit = (type: Mutation, payload?: unknown, options?: VuexCommitOptions) => void;
type InternalDispatch = (type: Action, payload?: unknown, options?: VuexDispatchOptions) => Promise<unknown>;

interface ActionContext<L, R> extends Omit<VuexActionContext<L, R>, 'commit' | 'dispatch'> {
  commit: InternalCommit;
  dispatch: InternalDispatch;
}

interface ExtendedStore<RootState> {
  readonly state: RootState;
  readonly getters: { [key in Getter]: unknown };
  dispatch: InternalDispatch;
}

declare module 'vue/types/vue' {
  interface Vue {
    $Store: ExtendedStore<RootState>;
    $package: TypedObject;
  }
}